import { format, parse, parseISO } from "date-fns";
import { LOCALE_CONFIG } from "./locale.js";

const DEFAULT_LOCALE = "en-CA";

function getConfig(locale) {
  return LOCALE_CONFIG[locale] || LOCALE_CONFIG[DEFAULT_LOCALE];
}

/**
 * Format a date for match display (e.g. "Wed Jan 29th" or "Wed 29 Jan").
 * Accepts Date objects, ISO strings, or yyyy-MM-dd strings.
 */
export function formatMatchDate(date, locale = DEFAULT_LOCALE) {
  const config = getConfig(locale);
  const d = ensureDate(date);
  return format(d, config.formats.matchDate, { locale: config.dateFnsLocale });
}

/**
 * Format a date for arrival display (e.g. "Jan 29th" or "29 Jan").
 */
export function formatArrivalDate(date, locale = DEFAULT_LOCALE) {
  const config = getConfig(locale);
  const d = ensureDate(date);
  return format(d, config.formats.arrivalDate, {
    locale: config.dateFnsLocale,
  });
}

/**
 * Format a full date/time (e.g. "Jan 29th 2026, 3:45 PM" or "29 Jan 2026, 3:45 PM").
 */
export function formatFullDateTime(date, locale = DEFAULT_LOCALE) {
  const config = getConfig(locale);
  const d = ensureDate(date);
  return format(d, config.formats.fullDateTime, {
    locale: config.dateFnsLocale,
  });
}

/**
 * Format a long date (e.g. "January 29, 2026" or "29 January 2026").
 */
export function formatLongDate(date, locale = DEFAULT_LOCALE) {
  const config = getConfig(locale);
  const d = ensureDate(date);
  return format(d, config.formats.longDate, { locale: config.dateFnsLocale });
}

/**
 * Format a date for compact tag display (e.g. "Th Jan 29th" or "Th 29 Jan").
 */
export function formatTagDate(date, locale = DEFAULT_LOCALE) {
  const config = getConfig(locale);
  const d = ensureDate(date);
  return format(d, config.formats.tagDate, { locale: config.dateFnsLocale });
}

/**
 * Format with a custom format string, using the locale's date-fns locale.
 */
export function formatLocalDate(date, locale = DEFAULT_LOCALE, formatString) {
  const config = getConfig(locale);
  const d = ensureDate(date);
  return format(d, formatString, { locale: config.dateFnsLocale });
}

/**
 * Coerce input to a Date object.
 * Handles: Date, ISO string, yyyy-MM-dd string.
 */
function ensureDate(date) {
  if (date instanceof Date) return date;
  if (typeof date === "string") {
    // yyyy-MM-dd format (common from DB)
    if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      return parse(date, "yyyy-MM-dd", new Date());
    }
    return parseISO(date);
  }
  return new Date(date);
}
